import { InternalAxiosRequestConfig } from "axios";
import { PRACTICE_SUPPLIER_ID, PRACTICE_ADDRESS_ID, PRACTICE_ADDRESS_FULL } from "./practiceOrder";
import { practiceSupplierName } from "./practiceMerchant";

/**
 * 練習模式的「帳號相關讀取端點」假資料，讓帳號子頁（付款方式、交易/對帳紀錄、成員、context 詳情）
 * 都有合理內容、不空白。純唯讀，回傳 response body（含外層 { data } 或 { state, data }）。
 */

const iso = (daysFromNow: number, time = "10:00:00") => {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}T${time}Z`;
};

// 練習家庭帳戶 context 詳情（accountDetail / inviteMember 用）
const contextDetail = () => ({
  context_id: 9001,
  context_name: "練習家庭帳戶",
  role: "owner",
  member_count: 2,
  max_members: 6,
  primary_supplier: { supplier_name: practiceSupplierName(), supplier_id: PRACTICE_SUPPLIER_ID },
  primary_address: { customer_address_id: PRACTICE_ADDRESS_ID, full_address: PRACTICE_ADDRESS_FULL, city: "台北市" },
  members: [
    { customer_id: 101, customer_name: "練習負責人", role: "owner" },
    { customer_id: 102, customer_name: "練習成員", role: "member" },
  ],
});

const card = (lastfour: string, issuer: string, mm: number, yyyy: number) => ({
  bincode: "412288",
  lastfour,
  issuer,
  expiry_month: mm,
  expiry_year: yyyy,
});

// 個人付款方式清單
const ownedPaymentMethods = () => [
  { payment_method_id: "pm-001", method_type: "credit_card", status: "active", nickname: "國泰世華 終身卡", card: card("8888", "國泰世華銀行", 12, 2027), create_time_stamp: iso(-160) },
  { payment_method_id: "pm-002", method_type: "credit_card", status: "active", nickname: "玉山 無限卡", card: card("4242", "玉山銀行", 3, 2028), create_time_stamp: iso(-120) },
];

// 帳戶可用付款方式（含 context 共用卡）
const contextPaymentMethods = () => [
  { payment_method_id: "pm-001", card_scope: "context", method_type: "credit_card", status: "active", nickname: "家庭共用卡", card: card("8888", "國泰世華銀行", 12, 2027), owner_customer_id: null, is_owned_by_caller: false, is_default: true, create_time_stamp: iso(-160) },
  { payment_method_id: "pm-002", card_scope: "personal", method_type: "credit_card", status: "active", nickname: "我的玉山卡", card: card("4242", "玉山銀行", 3, 2028), owner_customer_id: 101, is_owned_by_caller: true, is_default: false, create_time_stamp: iso(-120) },
];

// 單卡扣款紀錄
const cardPayments = () => [
  { payment_transaction_id: "pt-001", platform_order_id: "po-001", display_id: "ADO_8805", amount: 730, status: "captured", payment_method: "credit_card", provider: "tappay", card_label: "扣款", paid_at: iso(-5, "14:32:00"), created_at: iso(-5, "14:32:00") },
  { payment_transaction_id: "pt-002", platform_order_id: "po-002", display_id: "ADO_8804", amount: 1400, status: "captured", payment_method: "credit_card", provider: "tappay", card_label: "扣款", paid_at: iso(-12, "10:15:00"), created_at: iso(-12, "10:15:00") },
];

// 帳號付費紀錄
const paymentRecords = () => [
  { platform_order_id: "po-001", display_id: "ADO_8805", context_id: 9001, order_status: "completed", display_title: "練習家庭帳戶", display_subtitle: practiceSupplierName() + " · 信用卡", amount: 730, payment_method_label: "credit_card", created_at: iso(-5, "14:32:00"), paid_at: iso(-5, "14:32:00") },
  { platform_order_id: "po-002", display_id: "ADO_8804", context_id: 9001, order_status: "completed", display_title: "練習家庭帳戶", display_subtitle: practiceSupplierName() + " · 信用卡", amount: 1400, payment_method_label: "credit_card", created_at: iso(-12, "10:15:00"), paid_at: iso(-12, "10:15:00") },
  { platform_order_id: "po-003", display_id: "ADO_8803", context_id: 9001, order_status: "completed", display_title: "練習家庭帳戶", display_subtitle: practiceSupplierName() + " · 貨到付款", amount: 400, payment_method_label: "cash_on_delivery", created_at: iso(-20, "16:45:00"), paid_at: iso(-20, "17:30:00") },
];

// 帳戶訂單紀錄
const accountHistory = () => [
  { platform_order_id: "po-001", display_id: "ADO_8805", owner: { context_id: 9001, label: "練習家庭帳戶" }, supplier: { supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName() }, status: "completed", status_label: "訂單已完成", items_summary: [{ pn: "20kg", qty: 1 }], total: 730, payment_method_label: "信用卡", created_at: iso(-5, "14:32:00") },
  { platform_order_id: "po-002", display_id: "ADO_8804", owner: { context_id: 9001, label: "練習家庭帳戶" }, supplier: { supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName() }, status: "delivering", status_label: "正在配送中", items_summary: [{ pn: "50kg", qty: 1 }], total: 1600, payment_method_label: "信用卡", created_at: iso(-2, "11:00:00") },
  { platform_order_id: "po-003", display_id: "ADO_8803", owner: { context_id: 9001, label: "練習家庭帳戶" }, supplier: { supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName() }, status: "completed", status_label: "訂單已完成", items_summary: [{ pn: "10kg", qty: 1 }], total: 400, payment_method_label: "貨到付款", created_at: iso(-20, "09:20:00") },
];

// 帳戶付款 + 月結
const accountPayments = () => ({
  monthly_summary: { year_month: iso(0).slice(0, 7).replace("-", "/"), total_amount: 2730, transaction_count: 3 },
  items: [
    { platform_order_id: "po-001", display_id: "ADO_8805", context_id: 9001, order_status: "completed", order_status_label: "訂單已完成", supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName(), amount: 730, payment_method_label: "信用卡", payment_status: "captured", paid_at: iso(-5, "14:32:00"), created_at: iso(-5, "14:32:00") },
    { platform_order_id: "po-002", display_id: "ADO_8804", context_id: 9001, order_status: "delivering", order_status_label: "正在配送中", supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName(), amount: 1600, payment_method_label: "信用卡", payment_status: "captured", paid_at: iso(-2, "11:00:00"), created_at: iso(-2, "11:00:00") },
    { platform_order_id: "po-003", display_id: "ADO_8803", context_id: 9001, order_status: "completed", order_status_label: "訂單已完成", supplier_id: PRACTICE_SUPPLIER_ID, supplier_name: practiceSupplierName(), amount: 400, payment_method_label: "貨到付款", payment_status: "captured", paid_at: iso(-20, "09:20:00"), created_at: iso(-20, "09:20:00") },
  ],
  next_cursor: null,
  has_more: false,
});

/**
 * 處理帳號讀取端點；非此類回 null。須在 practiceApi 的 contexts 列表判斷「之前」呼叫，
 * 以攔截 contexts/:id 詳情；並在 resolveOrderRequest 之前呼叫，以攔截 platform-orders/account-*。
 */
export const resolveAccountRequest = (config: InternalAxiosRequestConfig): { data: unknown } | null => {
  const u = config.url || "";
  const method = (config.method || "get").toLowerCase();

  // 帳戶可用付款方式（context 範圍）
  if (u.includes("/app-api/customer/contexts/") && u.includes("/payment-methods")) {
    return { data: { items: contextPaymentMethods(), default_payment_method_id: "pm-001" } };
  }
  // context 詳情（排除 join / invite-code，已於上層處理）
  if (u.includes("/app-api/customer/contexts/") && !u.includes("/join") && !u.includes("/invite-code")) {
    return { data: contextDetail() };
  }

  // 個人付款方式
  if (u.includes("/app-api/customer/payment-methods")) {
    if (/\/payment-methods\/[^/]+\/payments/.test(u)) return { data: { items: cardPayments(), total: cardPayments().length } };
    if (method === "post") return { data: ownedPaymentMethods()[0] };
    if (/\/payment-methods\/[^/]+$/.test(u)) return { data: ownedPaymentMethods()[0] };
    return { data: { items: ownedPaymentMethods(), default_payment_method_id: "pm-001" } };
  }

  // 帳號付費紀錄
  if (u.includes("/app-api/customer/payment-records")) {
    return { data: { items: paymentRecords(), total: paymentRecords().length, page: 1, limit: 20 } };
  }
  // 帳戶訂單紀錄
  if (u.includes("/app-api/platform-orders/account-history")) {
    return { data: { items: accountHistory(), total_count: accountHistory().length, next_cursor: null, has_more: false } };
  }
  // 帳戶付款 + 月結
  if (u.includes("/app-api/platform-orders/account-payments")) {
    return { data: accountPayments() };
  }

  return null;
};
